import React, { useEffect, useState } from 'react';
import axios from 'axios';

/* istanbul ignore next -- REACT_APP_BACKEND_URL is inlined at build time */
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const EMPTY_FORM = {
  brand: 'sennelier_pastel',
  product_name: '',
  product_code: '',
  color_hex: '#8B5E4A',
  medium: 'soft_pastel',
  hardness: 'unknown',
  condition: 'full',
  status: 'owned',
  quantity: 1,
  notes: ''
};

const LABELS = {
  soft_pastel: 'Soft pastel',
  hard_pastel: 'Hard pastel',
  pastel_pencil: 'Pastel pencil',
  pan_pastel: 'PanPastel',
  oil_pastel: 'Oil pastel',
  other: 'Other',
  replacement_needed: 'Replace soon'
};

function InventoryPanel({ token, brands, onInventoryChanged, showToast }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const refreshInventory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/inventory`, { headers });
      const nextItems = response.data.items || [];
      setItems(nextItems);
      onInventoryChanged(nextItems);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Could not load your supplies', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshInventory();
  }, [token]);

  const updateForm = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const submitItem = async (event) => {
    event.preventDefault();
    setSaving(true);
    const payload = { ...form, quantity: Number(form.quantity) };

    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/inventory/${editingId}`, payload, { headers });
        showToast('Supply updated', 'success');
      } else {
        await axios.post(`${API_URL}/api/inventory`, payload, { headers });
        showToast('Supply added to your inventory', 'success');
      }
      resetForm();
      await refreshInventory();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Could not save this supply', 'error');
    } finally {
      setSaving(false);
    }
  };

  const editItem = (item) => {
    setEditingId(item.id);
    setForm({
      brand: item.brand,
      product_name: item.product_name,
      product_code: item.product_code || '',
      color_hex: item.color_hex,
      medium: item.medium,
      hardness: item.hardness,
      condition: item.condition,
      status: item.status,
      quantity: item.quantity,
      notes: item.notes || ''
    });
    setShowForm(true);
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Remove ${item.product_name} from your inventory?`)) return;

    try {
      await axios.delete(`${API_URL}/api/inventory/${item.id}`, { headers });
      showToast('Supply removed', 'success');
      await refreshInventory();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Could not remove this supply', 'error');
    }
  };

  const brandName = (brandId) => brands.find((brand) => brand.id === brandId)?.name || brandId;
  const ownedCount = items.filter((item) => item.status !== 'wishlist').length;

  return (
    <section className="bg-white rounded-xl shadow-lg p-6" aria-labelledby="inventory-heading">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="inventory-heading" className="text-xl font-semibold text-gray-800">My Pastel Inventory</h2>
          <p className="text-sm text-gray-600 mt-1">
            Add a swatch for each supply so matches can use what is already in your studio.
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {loading ? 'Loading supplies…' : `${ownedCount} owned · ${items.length - ownedCount} on your wishlist`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            if (showForm) resetForm();
            else setShowForm(true);
          }}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          {showForm ? 'Cancel' : 'Add supply'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submitItem} className="mt-5 p-4 rounded-lg bg-purple-50 border border-purple-100">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <label className="text-sm text-gray-700">
              Brand
              <select name="brand" value={form.brand} onChange={updateForm} className="mt-1 w-full p-2 border rounded-md bg-white">
                {brands.filter((brand) => brand.id !== 'general').map((brand) => (
                  <option key={brand.id} value={brand.id}>{brand.name}</option>
                ))}
              </select>
            </label>
            <label className="text-sm text-gray-700">
              Product name
              <input required name="product_name" value={form.product_name} onChange={updateForm} placeholder="e.g. Permanent Red" className="mt-1 w-full p-2 border rounded-md" />
            </label>
            <label className="text-sm text-gray-700">
              Product code
              <input name="product_code" value={form.product_code} onChange={updateForm} placeholder="Optional" className="mt-1 w-full p-2 border rounded-md" />
            </label>
            <label className="text-sm text-gray-700">
              Swatch color
              <span className="mt-1 flex gap-2">
                <input type="color" name="color_hex" value={form.color_hex} onChange={updateForm} className="h-10 w-12 p-1 border rounded-md bg-white" />
                <input required pattern="#[0-9A-Fa-f]{6}" name="color_hex" value={form.color_hex} onChange={updateForm} className="min-w-0 flex-1 p-2 border rounded-md font-mono" />
              </span>
            </label>
            <label className="text-sm text-gray-700">
              Medium
              <select name="medium" value={form.medium} onChange={updateForm} className="mt-1 w-full p-2 border rounded-md bg-white">
                {['soft_pastel', 'hard_pastel', 'pastel_pencil', 'pan_pastel', 'oil_pastel', 'other'].map((value) => (
                  <option key={value} value={value}>{LABELS[value]}</option>
                ))}
              </select>
            </label>
            <label className="text-sm text-gray-700">
              Hardness
              <select name="hardness" value={form.hardness} onChange={updateForm} className="mt-1 w-full p-2 border rounded-md bg-white">
                {['unknown', 'soft', 'medium', 'hard'].map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </label>
            <label className="text-sm text-gray-700">
              Condition
              <select name="condition" value={form.condition} onChange={updateForm} className="mt-1 w-full p-2 border rounded-md bg-white">
                {['full', 'half', 'broken', 'low'].map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </label>
            <label className="text-sm text-gray-700">
              Status
              <select name="status" value={form.status} onChange={updateForm} className="mt-1 w-full p-2 border rounded-md bg-white">
                {['owned', 'wishlist', 'replacement_needed'].map((value) => (
                  <option key={value} value={value}>{LABELS[value] || value}</option>
                ))}
              </select>
            </label>
            <label className="text-sm text-gray-700">
              Quantity
              <input type="number" min="1" max="99" name="quantity" value={form.quantity} onChange={updateForm} className="mt-1 w-full p-2 border rounded-md" />
            </label>
            <label className="text-sm text-gray-700 sm:col-span-2 lg:col-span-3">
              Notes
              <input name="notes" value={form.notes} onChange={updateForm} placeholder="Set, box, or anything useful" className="mt-1 w-full p-2 border rounded-md" />
            </label>
          </div>
          <div className="flex gap-2 mt-4">
            <button disabled={saving} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50">
              {saving ? 'Saving…' : editingId ? 'Update supply' : 'Add to inventory'}
            </button>
            {editingId && <button type="button" onClick={resetForm} className="px-4 py-2 border rounded-md bg-white">Cancel edit</button>}
          </div>
        </form>
      )}

      {!loading && items.length === 0 && !showForm && (
        <div className="mt-5 rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
          Your inventory is empty. Add your first pastel to enable owned-supply matching.
        </div>
      )}

      {items.length > 0 && (
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((item) => (
            <article key={item.id} className="flex gap-3 rounded-lg border border-gray-200 p-3">
              <div className="w-12 h-12 shrink-0 rounded-md border border-gray-300" style={{ backgroundColor: item.color_hex }} title={item.color_hex} />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-gray-800 truncate">{item.product_name} {item.product_code || ''}</p>
                <p className="text-xs text-gray-500 truncate">{brandName(item.brand)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {LABELS[item.medium]} · {item.condition} · qty {item.quantity}
                  {item.status !== 'owned' ? ` · ${LABELS[item.status] || item.status}` : ''}
                </p>
                <div className="flex gap-3 mt-2 text-xs">
                  <button type="button" onClick={() => editItem(item)} className="text-purple-700 hover:underline">Edit</button>
                  <button type="button" onClick={() => deleteItem(item)} className="text-red-600 hover:underline">Remove</button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

export default InventoryPanel;
